import React, { Suspense, useEffect } from 'react';
import { HashRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AccessibilityProvider } from "@/context/AccessibilityContext";
import LocalOnlyBanner from '@/components/shared/LocalOnlyBanner';
import { AppModeProvider } from "@/context/AppModeContext";
import { PageTransition } from "@/components/shared/PageTransition";
import { FocusIndicator } from "@/components/shared/FocusIndicator";
import { OfflineIndicator } from "@/components/shared/OfflineIndicator";
import { SkipLink } from "@/components/ui/SkipLink";
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { PwaUpdateToast } from '@/components/pwa/PwaUpdateToast';
import VersionUpdateBanner from '@/components/pwa/VersionUpdateBanner';
import LocalErrorBoundary from '@/components/LocalErrorBoundary';
import { Button } from '@/components/ui/button';
import { Loader2 } from "lucide-react";
import { FAF_ENABLED } from '@/lib/featureFlags';

// Lazy-load rarely-needed global UI
const VoiceCaptions = React.lazy(() => import("@/components/shared/VoiceCaptions"));
const MobileBottomNav = React.lazy(() => import("@/components/layout/MobileBottomNav"));
// Landing and core auth pages
const Landing = React.lazy(() => import("./pages/Landing"));
const Restaurants = React.lazy(() => import('./pages/Restaurants'));

// Restaurant landing pages
const RestaurantLanding = React.lazy(() => import('./pages/restaurant/RestaurantLanding'));
const RestaurantBusinessLanding = React.lazy(() => import('./pages/restaurant/RestaurantBusinessLanding'));
const RestaurantMenu = React.lazy(() => import('./pages/restaurant/RestaurantMenu'));
const RestaurantKDS = React.lazy(() => import('./pages/restaurant/RestaurantKDS'));
const RestaurantStaffLogin = React.lazy(() => import('./pages/restaurant/RestaurantStaffLogin'));
const RestaurantFOHDashboard = React.lazy(() => import('./pages/restaurant/RestaurantFOHDashboard'));

// Lazy-load auth pages so landing doesn't pay their cost
const Login = React.lazy(() => import("./pages/Login"));
const AuthCallback = React.lazy(() => import("./pages/AuthCallback"));

// Support / help pages
const GoogleSignInHelp = React.lazy(() => import('./pages/Support/GoogleSignInHelp'));

// Lazy-load dashboard and secondary pages
const Dashboard = React.lazy(() =>
  dynamicImportWithRetry(() => import("./pages/Dashboard"), 2, 350).catch((e) => {
    // eslint-disable-next-line no-console
    console.error('Failed to dynamically import Dashboard after retries:', e);
    return {
      default: function DashboardLoadError() {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="text-center max-w-md">
              <div className="text-2xl font-semibold mb-2 text-foreground">Failed to load Dashboard</div>
              <div className="text-sm text-foreground mb-4">
                This usually happens when the dev server restarted, the port changed, or the browser is still pointing to an old URL.
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button className="text-foreground" onClick={() => { window.location.reload(); }}>Try again</Button>
                <Button variant="ghost" className="text-foreground" onClick={() => { window.location.hash = '#/'; }}>Home</Button>
              </div>
            </div>
          </div>
        );
      }
    };
  })
);
const GameLobby = React.lazy(() => import("./pages/GameLobby"));
const JoinGame = React.lazy(() => import('./pages/JoinGame'));
const FafLobby = React.lazy(() => import('./pages/FafLobby'));
const Profile = React.lazy(() => import("./pages/Profile"));
const Leaderboard = React.lazy(() => import("./pages/Leaderboard"));
const GameHistory = React.lazy(() => import("./pages/GameHistory"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Contact = React.lazy(() => import("./pages/Contact"));
const LearnMore = React.lazy(() => import("./pages/LearnMore"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const TooltipProvider = React.lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({ default: m.TooltipProvider })),
);

const Toaster = React.lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);

const ReactQueryProvider = React.lazy(() => import("./providers/ReactQueryProvider"));

// Lazy-load game components for better code splitting and performance
const LudoGame = React.lazy(() =>
  dynamicImportWithRetry(() => import("./components/games/ludo/LudoGame"), 2, 350).catch((e) => {
    // eslint-disable-next-line no-console
    console.error('Failed to dynamically import LudoGame after retries:', e);
    return {
      default: function LudoLoadError() {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="text-center max-w-md">
              <div className="text-2xl font-semibold mb-2 text-foreground">Failed to load Ludo</div>
              <div className="text-sm text-foreground mb-4">
                This usually happens when the dev server restarted, the port changed, or a service worker cached an old dev asset.
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button className="text-foreground" onClick={() => { window.location.reload(); }}>Try again</Button>
                <Button variant="ghost" className="text-foreground" onClick={() => { window.location.hash = '#/'; }}>Home</Button>
              </div>
            </div>
          </div>
        );
      }
    };
  })
);
const ChessGame = React.lazy(() => import("./components/games/chess/ChessGame"));
const SnakesLaddersGame = React.lazy(() =>
  dynamicImportWithRetry(() => import("./components/games/snakes-ladders/SnakesLaddersGame"), 2, 350).catch((e) => {
    // eslint-disable-next-line no-console
    console.error('Failed to dynamically import SnakesLaddersGame after retries:', e);
    return {
      default: function SnakesLaddersLoadError() {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="text-center max-w-md">
              <div className="text-2xl font-semibold mb-2 text-foreground">Failed to load Snakes &amp; Ladders</div>
              <div className="text-sm text-foreground mb-4">
                This can happen if the dev server restarted, the port changed, or the browser cached an old module.
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button className="text-foreground" onClick={() => { window.location.reload(); }}>Try again</Button>
                <Button variant="ghost" className="text-foreground" onClick={() => { window.location.hash = '#/dashboard'; }}>Go to Dashboard</Button>
              </div>
            </div>
          </div>
        );
      }
    };
  })
);
const StrategoGame = React.lazy(() => import("./components/games/stratego/StrategoGame"));
function dynamicImportWithRetry<T>(importer: () => Promise<T>, attempts = 2, delayMs = 300): Promise<T> {
  let tries = 0;
  const attempt = async (): Promise<T> => {
    tries += 1;
    try {
      return await importer();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`dynamic import failed (attempt ${tries}):`, e);
      if (tries < attempts) {
        await new Promise((r) => setTimeout(r, delayMs));
        return attempt();
      }
      throw e;
    }
  };
  return attempt();
}

const UnoGame = React.lazy(() => dynamicImportWithRetry(() => import("./components/games/uno/UnoGame"), 2, 350).catch((e) => {
  // Surface import failures with clearer console info for debugging
  // eslint-disable-next-line no-console
  console.error('Failed to dynamically import UnoGame after retries:', e);
  try {
    import('@/lib/analytics').then((m) => {
      try { (m.default as any).trackEvent('DYNAMIC_IMPORT_FAILED', { module: 'uno', message: String(e && e.message), attempts: 2 }); } catch (err) {}
    });
  } catch (err) {}
  // Provide a lightweight fallback component so users get an actionable UI instead of a hard crash
  return {
    default: function UnoLoadError() {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md">
            <div className="text-2xl font-semibold mb-2 text-foreground">Failed to load UNO</div>
            <div className="text-sm text-foreground mb-4">Something went wrong while loading the UNO game. This can happen if the dev server or service worker is out of sync. We tried to recover automatically.</div>
            <div className="flex items-center justify-center gap-2">
              <Button className="text-foreground" onClick={() => { window.location.reload(); }}>Try again</Button>
              <Button variant="ghost" className="text-foreground" onClick={() => { window.location.hash = '#/dashboard'; }}>Go to Dashboard</Button>
            </div>
          </div>
        </div>
      );
    }
  };
}));
const DevGamiDemo = React.lazy(() => import('./pages/DevGamiDemo'));

// Lazy-load waitlist pages
const ManagerDashboard = React.lazy(() => import("./pages/waitlist/ManagerDashboard"));
const ManagerManagement = React.lazy(() => import("./pages/waitlist/ManagerManagement"));
// Manager tools pages
const ManagerStaff = React.lazy(() => import("./pages/manager/StaffPage"));
const ManagerMenuEditor = React.lazy(() => import("./pages/manager/MenuEditor"));
const ManagerOrders = React.lazy(() => import("./pages/manager/OrdersPage"));
const ManagerBills = React.lazy(() => import("./pages/manager/BillsPage"));
const GuestView = React.lazy(() => import("./pages/waitlist/GuestView"));
const PublicDisplay = React.lazy(() => import("./pages/waitlist/PublicDisplay"));
const AdminOnboarding = React.lazy(() => import("./pages/waitlist/AdminOnboarding"));
const SeedOwnerPage = React.lazy(() => import('./pages/admin/SeedOwner'));
const OwnersListPage = React.lazy(() => import('./pages/admin/OwnersList'));
const GamesDashboard = React.lazy(() => import("./pages/waitlist/GamesDashboard"));

// Loading fallback for lazy-loaded components
const GameLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
    <div className="text-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
      <p className="text-white text-lg">Loading game...</p>
    </div>
  </div>
);

const WaitlistLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const WaitlistAdminRedirect = () => <Navigate to="/restaurant/admin" replace />;

const WaitlistSubdomainRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/manager`} replace />;
};

const WaitlistSubdomainTrailingSlashRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/manager`} replace />;
};

const WaitlistGuestRedirect = () => {
  const { subdomain, tokenId } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/guest/${tokenId ?? ''}`} replace />;
};

const WaitlistGuestSessionRedirect = () => {
  const { subdomain, sessionKey } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/guest/s/${sessionKey ?? ''}`} replace />;
};

const WaitlistGamesRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/games`} replace />;
};

const WaitlistDisplayRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/display`} replace />;
};

const RestaurantFohAliasRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/restaurant/${subdomain ?? ''}/staff/foh`} replace />;
};

const AppRoutes = () => (
  <PageTransition>
    <Routes>
      <Route
        path="/"
        element={(
          <Suspense fallback={<PageLoadingFallback />}>
            <Landing />
          </Suspense>
        )}
      />
      <Route
        path="/restaurants"
        element={(
          <Suspense fallback={<PageLoadingFallback />}>
            <Restaurants />
          </Suspense>
        )}
      />
    <Route
      path="/login"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Login />
        </Suspense>
      )}
    />

    <Route
      path="/help/google-signin"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <GoogleSignInHelp />
        </Suspense>
      )}
    />
    <Route
      path="/auth/callback"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <AuthCallback />
        </Suspense>
      )}
    />

    {/* Support pages */}
    <Route
      path="/help/google-signin"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <GoogleSignInHelp />
        </Suspense>
      )}
    />
    <Route
      path="/dashboard"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Dashboard />
        </Suspense>
      )}
    />
    <Route
      path="/game/:gameId"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <GameLobby />
        </Suspense>
      )}
    />
    <Route
      path="/play/ludo"
      element={(
        <Suspense fallback={<GameLoadingFallback />}>
          <LudoGame />
        </Suspense>
      )}
    />
    <Route
      path="/play/chess"
      element={(
        <Suspense fallback={<GameLoadingFallback />}>
          <ChessGame />
        </Suspense>
      )}
    />
    <Route
      path="/play/snakes-ladders"
      element={(
        <Suspense fallback={<GameLoadingFallback />}>
          <SnakesLaddersGame />
        </Suspense>
      )}
    />
    <Route
      path="/play/stratego"
      element={(
        <Suspense fallback={<GameLoadingFallback />}>
          <StrategoGame />
        </Suspense>
      )}
    />
    <Route
      path="/play/uno"
      element={(
        <Suspense fallback={<GameLoadingFallback />}>
          <UnoGame />
        </Suspense>
      )}
    />

    <Route
      path="/join"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          {FAF_ENABLED ? <JoinGame /> : <NotFound />}
        </Suspense>
      )}
    />

    {/* Compatibility: some clients produce hashbang URLs like `#!/join?...` */}
    <Route
      path="/!/join"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          {FAF_ENABLED ? <JoinGame /> : <NotFound />}
        </Suspense>
      )}
    />

    <Route
      path="/faf"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          {FAF_ENABLED ? <FafLobby /> : <NotFound />}
        </Suspense>
      )}
    />

    {/* Compatibility: some clients produce hashbang URLs like `#!/faf?...` */}
    <Route
      path="/!/faf"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          {FAF_ENABLED ? <FafLobby /> : <NotFound />}
        </Suspense>
      )}
    />
    {/* Restaurant routes */}
    <Route
      path="/restaurant"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantLanding />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/admin"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <AdminOnboarding />
        </Suspense>
      )}
    />
    <Route
      path="/admin/seed-owner"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <SeedOwnerPage />
        </Suspense>
      )}
    />
    <Route
      path="/admin/owners"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <OwnersListPage />
        </Suspense>
      )}
    />
    <Route path="/admin" element={<WaitlistAdminRedirect />} />
    <Route
      path="/restaurant/:subdomain"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantBusinessLanding />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/menu"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantMenu />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/kds"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantKDS />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/staff"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantStaffLogin />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/staff/foh"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantFOHDashboard />
        </Suspense>
      )}
    />
    <Route path="/restaurant/:subdomain/foh" element={<RestaurantFohAliasRedirect />} />
    <Route path="/restaurant/:subdomain/foh/" element={<RestaurantFohAliasRedirect />} />
    <Route
      path="/restaurant/:subdomain/manager"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerDashboard />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/manage"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerManagement />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/manage/staff"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerStaff />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/manage/menu"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerMenuEditor />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/manage/orders"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerOrders />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/manage/bills"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <ManagerBills />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantBusinessLanding />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/guest/:tokenId"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <GuestView />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/guest/s/:sessionKey"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <GuestView />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/guest/:tokenId/menu"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantMenu />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/guest/s/:sessionKey/menu"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <RestaurantMenu />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/games"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <GamesDashboard />
        </Suspense>
      )}
    />
    <Route
      path="/restaurant/:subdomain/display"
      element={(
        <Suspense fallback={<WaitlistLoadingFallback />}>
          <PublicDisplay />
        </Suspense>
      )}
    />

    {/* Backwards-compatible waitlist URLs (redirects) */}
    <Route path="/waitlist/admin" element={<WaitlistAdminRedirect />} />
    <Route path="/waitlist/:subdomain" element={<WaitlistSubdomainRedirect />} />
    <Route path="/waitlist/:subdomain/" element={<WaitlistSubdomainTrailingSlashRedirect />} />
    <Route path="/waitlist/:subdomain/guest/:tokenId" element={<WaitlistGuestRedirect />} />
    <Route path="/waitlist/:subdomain/guest/s/:sessionKey" element={<WaitlistGuestSessionRedirect />} />
    <Route path="/waitlist/:subdomain/games" element={<WaitlistGamesRedirect />} />
    <Route path="/waitlist/:subdomain/display" element={<WaitlistDisplayRedirect />} />
    <Route
      path="/profile"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Profile />
        </Suspense>
      )}
    />
    <Route
      path="/leaderboard"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Leaderboard />
        </Suspense>
      )}
    />
    <Route
      path="/game-history"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <GameHistory />
        </Suspense>
      )}
    />
    <Route
      path="/privacy"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Privacy />
        </Suspense>
      )}
    />
    <Route
      path="/terms"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Terms />
        </Suspense>
      )}
    />
    <Route
      path="/contact"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <Contact />
        </Suspense>
      )}
    />
    <Route
      path="/learn-more"
      element={(
        <Suspense fallback={<PageLoadingFallback />}>
          <LearnMore />
        </Suspense>
      )}
    />
    {import.meta.env.VITE_GAMIFICATION === 'true' && (
      <Route
        path="/dev/gami-demo"
        element={(
          <Suspense fallback={<PageLoadingFallback />}>
            <DevGamiDemo />
          </Suspense>
        )}
      />
    )}
      <Route
        path="*"
        element={(
          <Suspense fallback={<PageLoadingFallback />}>
            <NotFound />
          </Suspense>
        )}
      />
    </Routes>
  </PageTransition>
);

const App = () => {
  const [afterFirstRender, setAfterFirstRender] = React.useState(false);

  React.useEffect(() => {
    setAfterFirstRender(true);
  }, []);

  const RouterBody = () => {
    const location = useLocation();
      useEffect(() => {
        // Interrupt any ongoing narration when navigation occurs
        // to avoid speech continuing after leaving a game.
        try {
          import('@/lib/narration').then(m => m.interruptNarration());
        } catch (e) {}
      }, [location.pathname]);
    const needsReactQuery = location.pathname !== "/";

    const appContent = (
      <>
        <SkipLink targetId="main-content" />
        <FocusIndicator />
        <OfflineIndicator />
        <LocalOnlyBanner />

        {afterFirstRender && (
          <LocalErrorBoundary component="InstallPrompt">
            <InstallPrompt />
          </LocalErrorBoundary>
        )}

        {afterFirstRender && (
          <Suspense fallback={null}>
            <Toaster />
          </Suspense>
        )}

        {afterFirstRender && (
          <Suspense fallback={null}>
            <VersionUpdateBanner />
          </Suspense>
        )}

        {afterFirstRender && (
          <Suspense fallback={null}>
            <PwaUpdateToast />
          </Suspense>
        )}

        <main id="main-content" tabIndex={-1} className="outline-none">
          <AppRoutes />
        </main>

        {afterFirstRender && (
          <Suspense fallback={null}>
            <VoiceCaptions />
          </Suspense>
        )}

        {afterFirstRender && (
          <Suspense fallback={null}>
            <MobileBottomNav />
          </Suspense>
        )}
      </>
    );

    if (!needsReactQuery) {
      return <AppModeProvider>{appContent}</AppModeProvider>;
    }

    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <ReactQueryProvider>
          <AppModeProvider>{appContent}</AppModeProvider>
        </ReactQueryProvider>
      </Suspense>
    );
  };

  const routerContent = (
    <HashRouter>
      <RouterBody />
    </HashRouter>
  );

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AccessibilityProvider>
            {afterFirstRender ? (
              <Suspense fallback={routerContent}>
                <TooltipProvider>{routerContent}</TooltipProvider>
              </Suspense>
            ) : (
              routerContent
            )}
          </AccessibilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
